// Файл table_column_settings_store.js

/**
 * Модуль содержит логику таблиц для настроек интерфейса.
 * Функция `visible_columns_cache` обрабатывает данные своего сценария.
 * Функция `load_promises` загружает данные из соответствующего
 * источника.
 * Запросы данных выполняются через существующее взаимодействие с API.
 */

import { apiService } from '../../services/api';



const visible_columns_cache = new Map();
const load_promises = new Map();
let full_profile_loaded = false;

const normalize_table_id = (value) => String(value ?? '').trim();

const normalize_visible_columns = (value) => {
    if (!Array.isArray(value)) {
        return null;
    }

    return [...new Set(value.map((column) => (
        typeof column === 'number' ? column : String(column)
    )))];
};

const clone_visible_columns = (value) => (
    Array.isArray(value) ? [...value] : null
);


export const install_table_visible_columns = (value = {}) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return;
    }

    visible_columns_cache.clear();
    full_profile_loaded = true;

    Object.entries(value).forEach(([table_id, visible_columns]) => {
        const normalized_table_id = normalize_table_id(table_id);
        const normalized_columns = normalize_visible_columns(
            visible_columns
        );

        if (normalized_table_id && normalized_columns !== null) {
            visible_columns_cache.set(
                normalized_table_id,
                normalized_columns,
            );
        }
    });
};

export const load_table_visible_columns = async (table_id) => {
    const normalized_table_id = normalize_table_id(table_id);

    if (!normalized_table_id) {
        return null;
    }

    if (visible_columns_cache.has(normalized_table_id)) {
        return clone_visible_columns(
            visible_columns_cache.get(normalized_table_id)
        );
    }

    if (full_profile_loaded) {
        visible_columns_cache.set(normalized_table_id, null);

        return null;
    }

    if (load_promises.has(normalized_table_id)) {
        return load_promises.get(normalized_table_id);
    }

    const load_promise = apiService.df({
        table_name: 'interface_customization_settings',
        params: {
            action: 'load_table_columns',
            table_id: normalized_table_id,
        },
    }).then((response) => {
        const visible_columns = normalize_visible_columns(
            response?.visible_columns
        );
        visible_columns_cache.set(
            normalized_table_id,
            visible_columns,
        );

        return clone_visible_columns(visible_columns);

    }).finally(() => {
        load_promises.delete(normalized_table_id);
    });

    load_promises.set(normalized_table_id, load_promise);

    return load_promise;
};

export const save_table_visible_columns = async (
    table_id,
    visible_columns,
) => {
    const normalized_table_id = normalize_table_id(table_id);
    const normalized_columns = normalize_visible_columns(visible_columns);

    if (!normalized_table_id || normalized_columns === null) {
        return normalized_columns;
    }

    visible_columns_cache.set(
        normalized_table_id,
        normalized_columns,
    );

    const response = await apiService.df({
        table_name: 'interface_customization_settings',
        params: {
            action: 'save_table_columns',
            table_id: normalized_table_id,
        },
        table_data: normalized_columns,
    });
    const saved_columns = normalize_visible_columns(
        response?.visible_columns
    ) || normalized_columns;

    visible_columns_cache.set(
        normalized_table_id,
        saved_columns,
    );

    return clone_visible_columns(saved_columns);
};
